import BookmarkPlusDefaultIcon from "@/assets/images/icons/bookmark-plus-default.svg";
import BookmarkPlusHoverIcon from "@/assets/images/icons/bookmark-plus-hover.svg";
import BookmarkPlusPressedIcon from "@/assets/images/icons/bookmark-plus-pressed.svg";
import { BookmarkSave } from "@/components/BookmarkSave";
import { CoursePageShell } from "@/components/course/CoursePageShell";
import { CourseSearchBar } from "@/components/course/CourseSearchBar";
import { LessonPreview } from "@/components/course/LessonPreview";
import { SetHomeDashboardButton } from "@/components/home-dashboard";
import { SubscriptionHubCtas } from "@/components/subscription/SubscriptionHubCtas";
import { LessonCard } from "@/components/lesson/LessonCard";
import { LessonListView } from "@/components/lesson/LessonListView";
import {
  CollectionCard,
  CourseThumbnailsSection,
  FullWidthSection,
  SkillShortcuts,
  StaticSection,
  useErrorHandler,
} from "@/components/ui";
import { useDashboardCourseData } from "@/hooks/useDashboardCourseData";
import { useHideLessonFromContinue } from "@/hooks/useHideLessonFromContinue";
import { useSearchTracking } from "@/hooks/useSearchTracking";
import { useAuth } from "@/lib/AuthContext";
import { CategoryType, CourseStatus, LIMITS } from "@/lib/constants";
import { lessonSearchPlaceholder } from "@/lib/courseSearchPlaceholder";
import { dashboardShowsTrialCta, TRIAL_CTA_TARGET } from "@/lib/trialCourseCta";
import {
  DASHBOARD_TOOLBAR_ARTWORK_PX,
  dashboardToolbarArtworkButton,
} from "@/lib/dashboardToolbarUi";
import {
  CONTENT_MAX_WIDTH,
  useIsMobile,
  useLessonCardLayout,
} from "@/lib/useDeviceType";
import { isCourseBookmarked } from "@/supabase/services";
import {
  searchLessonsByFulltext,
  searchLessonsInCourseByLike,
} from "@/supabase/services/lesson";
import type { Collection, Course, Lesson, Section, UserLesson } from "@/types";
import { buildLessonUrl } from "@/utils/buildLessonUrl";
import { shouldSkipPreview as shouldSkipPreviewUtil } from "@/utils/lessonHelpers";
import { useRouter } from "expo-router";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Platform } from "react-native";
import { Button, XStack, YStack } from "tamagui";

// --------------- Memoized dashboard section rows ---------------

type LessonCardLayout = ReturnType<typeof useLessonCardLayout>;

type DashboardCollectionSectionProps = {
  lessons: Lesson[];
  onCollectionPress: (item: Collection) => void;
};

const DashboardCollectionSection = React.memo(function DashboardCollectionSection({
  lessons,
  onCollectionPress,
}: DashboardCollectionSectionProps) {
  const data = useMemo(
    () =>
      lessons.map((l) => ({
        id: String(l.id),
        collectionTitle: l.title,
        imageUrl: (l as any).thumbnail || (l as any).imageUrl || "",
        bgColor: (l as any).color || undefined,
        slug: l.slug,
      })),
    [lessons],
  );

  const onCollectionPressRef = React.useRef(onCollectionPress);
  onCollectionPressRef.current = onCollectionPress;

  const renderItem = useCallback(
    ({ item }: { item: Collection }) => (
      <CollectionCard
        id={item.id}
        slug={item.slug}
        collectionTitle={item.collectionTitle}
        imageUrl={item.imageUrl}
        bgColor={item.bgColor}
        onPress={() => onCollectionPressRef.current(item)}
      />
    ),
    [],
  );

  return (
    <FullWidthSection title="Collections" data={data} renderItem={renderItem} />
  );
});

type DashboardCourseCategorySectionProps = {
  title: string;
  lessons: Lesson[];
  courseProgressMap: Record<number, number>;
  onPressCourse: (slug: string) => void;
};

const DashboardCourseCategorySection = React.memo(
  function DashboardCourseCategorySection({
    title,
    lessons,
    courseProgressMap,
    onPressCourse,
  }: DashboardCourseCategorySectionProps) {
    const data = useMemo(
      () =>
        lessons.map((l) => {
          const p = courseProgressMap[l.id];
          return {
            id: l.id,
            slug: l.slug,
            thumbnail: (l as any).thumbnail ?? null,
            progressPercentage: p !== undefined ? p * 100 : 0,
          };
        }),
      [lessons, courseProgressMap],
    );

    return (
      <CourseThumbnailsSection
        title={title}
        data={data}
        onPressCourse={onPressCourse}
      />
    );
  },
);

type DashboardStaticLessonSectionProps = {
  section: Section;
  userLessonsMap: Record<number, UserLesson>;
  lessonCardLayout: LessonCardLayout;
  onLessonPress: (l: Lesson) => void;
  onContinuePress: (l: Lesson) => void;
  onContinueRemove?: (l: Lesson) => void;
  onSectionSeeAll: (section: Section) => void;
};

const DashboardStaticLessonSection = React.memo(function DashboardStaticLessonSection({
  section,
  userLessonsMap,
  lessonCardLayout,
  onLessonPress,
  onContinuePress,
  onContinueRemove,
  onSectionSeeAll,
}: DashboardStaticLessonSectionProps) {
  const isContinue = section.slug === "continue";

  const renderItem = useCallback(
    ({ item }: { item: Lesson }) => (
      <LessonCard
        item={item}
        userLesson={userLessonsMap[item.id]}
        onPress={() =>
          isContinue ? onContinuePress(item) : onLessonPress(item)
        }
        onRemove={
          isContinue && onContinueRemove
            ? () => onContinueRemove(item)
            : undefined
        }
        {...lessonCardLayout}
      />
    ),
    [
      userLessonsMap,
      isContinue,
      onContinuePress,
      onContinueRemove,
      onLessonPress,
      lessonCardLayout,
    ],
  );

  const handleSeeAll = useCallback(() => {
    onSectionSeeAll(section);
  }, [onSectionSeeAll, section]);

  return (
    <YStack w="100%" maw={CONTENT_MAX_WIDTH} mx="auto">
      <StaticSection
        title={section.title}
        data={section.lessons}
        renderItem={renderItem}
        onSeeAll={section.slug ? handleSeeAll : undefined}
      />
    </YStack>
  );
});

type DashboardCourseSectionsProps = {
  sections: Section[];
  courseSlug: string;
  courseTitle: string;
  userLessonsMap: Record<number, UserLesson>;
  courseProgressMap: Record<number, number>;
  lessonCardLayout: LessonCardLayout;
  onCollectionPress: (item: Collection) => void;
  onCoursePress: (slug: string) => void;
  onLessonPress: (l: Lesson) => void;
  onContinuePress: (l: Lesson) => void;
  onContinueRemove?: (l: Lesson) => void;
  onSectionSeeAll: (section: Section) => void;
};

const DashboardCourseSections = React.memo(function DashboardCourseSections({
  sections,
  courseSlug,
  courseTitle,
  userLessonsMap,
  courseProgressMap,
  lessonCardLayout,
  onCollectionPress,
  onCoursePress,
  onLessonPress,
  onContinuePress,
  onContinueRemove,
  onSectionSeeAll,
}: DashboardCourseSectionsProps) {
  return (
    <YStack>
      {sections.map((section, index) => {
        const sectionKey = String(section.id || `section-${index}`);

        if (
          section.type === CategoryType.SkillPathwaySection &&
          section.pathways?.length
        ) {
          return (
            <SkillShortcuts
              key={sectionKey}
              pathways={section.pathways}
              courseSlug={courseSlug}
              courseTitle={courseTitle}
            />
          );
        }

        if (section.type === CategoryType.Collection) {
          return (
            <DashboardCollectionSection
              key={sectionKey}
              lessons={section.lessons}
              onCollectionPress={onCollectionPress}
            />
          );
        }

        if (section.type === CategoryType.CourseCategory) {
          return (
            <DashboardCourseCategorySection
              key={sectionKey}
              title={section.title?.trim() || "Courses"}
              lessons={section.lessons}
              courseProgressMap={courseProgressMap}
              onPressCourse={onCoursePress}
            />
          );
        }

        return (
          <DashboardStaticLessonSection
            key={sectionKey}
            section={section}
            userLessonsMap={userLessonsMap}
            lessonCardLayout={lessonCardLayout}
            onLessonPress={onLessonPress}
            onContinuePress={onContinuePress}
            onContinueRemove={onContinueRemove}
            onSectionSeeAll={onSectionSeeAll}
          />
        );
      })}
    </YStack>
  );
});

// --------------- Dashboard Course View ---------------

type DashboardCourseViewProps = {
  course: Course;
  courseSlug: string;
  courseId?: string;
  minStatus: number;
};

export function DashboardCourseView({
  course,
  courseSlug,
  courseId,
  minStatus,
}: DashboardCourseViewProps) {
  const { user } = useAuth();
  const router = useRouter();
  const isMobile = useIsMobile();
  const lessonCardLayout = useLessonCardLayout();
  const { showError, showSuccess } = useErrorHandler();

  const courseAvatarUrl =
    (isMobile
      ? course.avatar_url_mobile?.trim()
      : course.avatar_url_desktop?.trim()) || null;

  // Per-course toggle (CMS). Backfilled true for existing dashboard courses, so
  // their lesson dates keep showing unless someone turns the toggle off.
  const hidePublishedDate = !course.metadata?.show_lesson_dates;

  const {
    sections,
    userLessonsMap,
    courseProgressMap,
    linkedCourseSlugsMap,
    isLoading,
    error,
    queries,
  } = useDashboardCourseData(
    course.id,
    user?.id,
    courseSlug,
    LIMITS.DASHBOARD_LATEST,
    minStatus,
  );

  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Lesson[]>([]);
  const lastQueriedSearchRef = React.useRef<string | null>(null);

  const [refreshing, setRefreshing] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isCourseBookmarkedState, setIsCourseBookmarkedState] = useState(false);
  const [isBookmarkHovered, setIsBookmarkHovered] = useState(false);

  const courseIdNum = course.id != null ? Number(course.id) : undefined;

  const searchTracking = useSearchTracking({
    scope: "in_course",
    courseId: courseIdNum,
    courseSlug: String(courseSlug),
    courseTitle: course.title,
  });

  useEffect(() => {
    if (!user?.id || courseIdNum == null) return;
    isCourseBookmarked(user.id, courseIdNum)
      .then(setIsCourseBookmarkedState)
      .catch(() => {});
  }, [user?.id, courseIdNum]);

  const refetchAll = useCallback(async () => {
    await Promise.all([
      queries.sectionsQuery.refetch(),
      queries.collectionsQuery.refetch(),
      queries.courseThumbnailsQuery.refetch(),
      queries.latestLessonsQuery.refetch(),
      queries.latestCategoryQuery.refetch(),
      queries.linkedCoursesQuery.refetch(),
      queries.userLessonsQuery.refetch(),
      queries.continueLessonsQuery.refetch(),
      queries.courseProgressQuery.refetch(),
    ]);
  }, [queries]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await refetchAll();
    } finally {
      setRefreshing(false);
    }
  }, [refetchAll]);

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (course.metadata?.allow_lesson_search !== true) {
        setSearchResults([]);
        lastQueriedSearchRef.current = null;
        setSearching(false);
        return;
      }
      const q = searchQuery.trim();
      if (q.length === 0) {
        setSearchResults([]);
        lastQueriedSearchRef.current = null;
        return;
      }
      if (q.length < 2) {
        setSearchResults([]);
        return;
      }
      if (lastQueriedSearchRef.current === q) return;

      try {
        setSearching(true);
        if (!course?.id) {
          setSearchResults([]);
          return;
        }
        let results = await searchLessonsByFulltext(q, course.id, minStatus);
        if (results.length === 0) {
          results = await searchLessonsInCourseByLike(
            course.id,
            q,
            20,
            minStatus,
          );
        }
        setSearchResults(results);
        lastQueriedSearchRef.current = q;
        searchTracking.onResultsSettled(
          q,
          results.map((l) => ({
            id: l.id,
            type: "lesson" as const,
            slug: l.slug,
            title: l.title,
            courseSlug: l.course_slug ?? String(courseSlug),
          })),
        );
      } catch (err: any) {
        showError(err?.message || "Search failed");
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(handler);
  }, [
    searchQuery,
    course,
    showError,
    minStatus,
    course.metadata?.allow_lesson_search,
    searchTracking,
    courseSlug,
  ]);

  const navigateToLesson = useCallback((l: Lesson) => {
    const slug = l.course_slug ?? courseSlug;
    router.push(
      buildLessonUrl({
        courseSlug: String(slug),
        lessonSlug: l.slug,
        referrer: l.course_slug ? `/courses/${courseSlug}` : undefined,
        referrerTitle: l.course_slug ? course.title : undefined,
        courseTitle: course.title,
      }) as any,
    );
  }, [router, courseSlug, course.title]);

  const handleLessonPress = useCallback((l: Lesson) => {
    if (shouldSkipPreviewUtil(l, userLessonsMap)) {
      navigateToLesson(l);
      return;
    }
    setLesson(l);
    if (isMobile) {
      setModalOpen(false);
      setSheetOpen(true);
    } else {
      setSheetOpen(false);
      setModalOpen(true);
    }
  }, [userLessonsMap, navigateToLesson, isMobile]);

  const handleSearchResultLessonPress = useCallback(
    (l: Lesson) => {
      const position = searchResults.findIndex((r) => r.id === l.id) + 1;
      searchTracking.trackResultClick(
        searchQuery,
        {
          id: l.id,
          type: "lesson",
          slug: l.slug,
          title: l.title,
          courseSlug: l.course_slug ?? String(courseSlug),
        },
        position,
      );
      handleLessonPress(l);
    },
    [searchResults, searchQuery, searchTracking, courseSlug, handleLessonPress],
  );

  const handleContinuePress = useCallback((l: Lesson) => {
    if (l.course_slug) {
      router.push(buildLessonUrl({ courseSlug: l.course_slug, lessonSlug: l.slug }) as any);
      return;
    }
    handleLessonPress(l);
  }, [router, handleLessonPress]);

  const hideLessonFromContinue = useHideLessonFromContinue(user?.id);
  const handleContinueRemove = useCallback((l: Lesson) => {
    hideLessonFromContinue.mutate(l.id, {
      onSuccess: () => showSuccess("Removed from Continue watching"),
      onError: (err) =>
        showError(
          err instanceof Error ? err.message : "Failed to remove lesson",
        ),
    });
  }, [hideLessonFromContinue, showSuccess, showError]);

  const handleCollectionPress = useCallback((item: Collection) => {
    router.push({
      pathname: "/courses/[course-slug]/collections/[collection-slug]",
      params: {
        "course-slug": String(courseSlug),
        "collection-slug": String(item.slug),
      },
    });
  }, [router, courseSlug]);

  const handleCoursePress = useCallback((slug: string) => {
    router.push({
      pathname: "/courses/[course-slug]",
      params: { "course-slug": slug },
    });
  }, [router]);

  const handleSectionSeeAll = useCallback((section: Section) => {
    const linkedSlug = section.id ? linkedCourseSlugsMap[section.id] : undefined;
    if (section.type === CategoryType.LinkedCourseSection && linkedSlug) {
      router.push(`/courses/${linkedSlug}` as any);
      return;
    }
    router.push({
      pathname: "/courses/[course-slug]/collections/[collection-slug]",
      params: {
        "course-slug": String(courseSlug),
        "collection-slug": String(section.slug),
      },
    });
  }, [router, courseSlug, linkedCourseSlugsMap]);

  const handleSearchClear = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    lastQueriedSearchRef.current = null;
  }, []);

  const showSearchResults =
    course.metadata?.allow_lesson_search === true &&
    searchQuery.trim().length >= 2;

  return (
    <>
      <CoursePageShell
        courseId={course.id ? String(course.id) : courseId}
        courseTitle={course.title}
        courseSummary={course.summary ?? null}
        courseAvatarUrl={courseAvatarUrl}
        courseSkillLevel={course.skill_level ?? null}
        isLoading={isLoading}
        error={error}
        onRetry={refetchAll}
        refreshing={refreshing}
        onRefresh={onRefresh}
        courseStatus={course.status}
        canViewDrafts={minStatus < CourseStatus.Published}
      >
        {/* Jazz Piano First Steps trial CTA on the piano level 1/2 home dashboards (JPF, JPE). */}
        {user && dashboardShowsTrialCta(course.metadata) ? (
          <YStack
            w="100%"
            maxWidth={CONTENT_MAX_WIDTH}
            alignSelf="center"
            px={16}
            pt="$3"
          >
            <SubscriptionHubCtas userId={user.id} only={[TRIAL_CTA_TARGET]} />
          </YStack>
        ) : null}
        <CourseSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          searching={searching}
          showLessonSearch={course.metadata?.allow_lesson_search === true}
          searchPlaceholder={lessonSearchPlaceholder(course.metadata)}
          onClear={handleSearchClear}
          courseSlug={courseSlug}
          rightContent={
            user ? (
              <XStack ai="center" gap="$3">
                <SetHomeDashboardButton
                  courseTitle={course.title}
                  courseSlug={courseSlug}
                  courseMetadata={course.metadata}
                />
                {courseIdNum != null ? (
                  <BookmarkSave
                    mode="course"
                    onSave={setIsCourseBookmarkedState}
                    showError={showError}
                    showSuccess={showSuccess}
                    courseId={courseIdNum}
                    courseUrl={`/courses/${courseSlug}`}
                    placement="bottom-end"
                  >
                    <Button
                      {...dashboardToolbarArtworkButton}
                      onMouseEnter={
                        Platform.OS === "web"
                          ? () => setIsBookmarkHovered(true)
                          : undefined
                      }
                      onMouseLeave={
                        Platform.OS === "web"
                          ? () => setIsBookmarkHovered(false)
                          : undefined
                      }
                    >
                      {isCourseBookmarkedState ? (
                        <BookmarkPlusPressedIcon
                          width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                          height={DASHBOARD_TOOLBAR_ARTWORK_PX}
                        />
                      ) : isBookmarkHovered ? (
                        <BookmarkPlusHoverIcon
                          width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                          height={DASHBOARD_TOOLBAR_ARTWORK_PX}
                        />
                      ) : (
                        <BookmarkPlusDefaultIcon
                          width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                          height={DASHBOARD_TOOLBAR_ARTWORK_PX}
                        />
                      )}
                    </Button>
                  </BookmarkSave>
                ) : null}
              </XStack>
            ) : undefined
          }
          discussionUrl={course.discussion_url ?? null}
        />

        <YStack>
        {showSearchResults ? (
            <LessonListView
              lessons={searchResults}
              onPressLesson={handleSearchResultLessonPress}
              isMobile={isMobile}
              searching={searching}
              showResultsCount
              hidePublishedDate={hidePublishedDate}
            />
          ) : (
            <DashboardCourseSections
              sections={sections}
              courseSlug={courseSlug}
              courseTitle={course.title}
              userLessonsMap={userLessonsMap}
              courseProgressMap={courseProgressMap}
              lessonCardLayout={lessonCardLayout}
              onCollectionPress={handleCollectionPress}
              onCoursePress={handleCoursePress}
              onLessonPress={handleLessonPress}
              onContinuePress={handleContinuePress}
              onContinueRemove={user ? handleContinueRemove : undefined}
              onSectionSeeAll={handleSectionSeeAll}
            />
          )}
        </YStack>
      </CoursePageShell>

      <LessonPreview
        lesson={lesson}
        sheetOpen={sheetOpen}
        modalOpen={modalOpen}
        onSheetChange={setSheetOpen}
        onModalChange={setModalOpen}
        onPlayPress={() => {
          setModalOpen(false);
          setSheetOpen(false);
        }}
        userLessonsMap={userLessonsMap}
        courseSlug={lesson?.course_slug ?? courseSlug}
        userId={user?.id}
        hidePublishedDate={hidePublishedDate}
        showError={showError}
        showSuccess={showSuccess}
      />
    </>
  );
}
